import { useCallback, useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Card from "components/card";
import ExpandableDataTable, { DetailGrid, DetailItem } from "components/admin/ExpandableDataTable";
import InputField from "components/fields/InputField";
import ConfirmDialog from "components/admin/ConfirmDialog";
import StatusBadge from "components/admin/StatusBadge";
import { apiGet, apiPost, apiPut } from "lib/apiClient";
import { formatKes } from "lib/format";
import { useAuth } from "contexts/AuthContext";
import { PeriodPicker, bpsLabel, usePartnerRevenueBreakdown } from "components/revenue/RevenueStatement";
import { PartnerOversight } from "components/backoffice/Backoffice";
import { PARTNER_ROOT_DOMAIN, normalizeSlugInput, partnerPageUrls, slugFromName, slugProblem, validSharePercent } from "lib/partnerPages";
import PartnerProfileEditor from "./PartnerProfileEditor";

const columnHelper = createColumnHelper();

const SHARE_HELP = "Of your 80% share of the net revenue this partner's markets earn. You keep the rest.";
const MAIN_SHARE_LABEL = "Include trades on main Wasaa markets made via this partner's subdomain/page in revenue share";
const MAIN_SHARE_HELP = "Of your 80% of the trading fees on your own (main) markets, for positions opened from this partner's page or subdomain.";
const EMPTY_FORM = { name: "", slug: "", slugTouched: false, contactName: "", contactEmail: "", contactPhone: "", sharePercent: "30", mainEnabled: false, mainSharePercent: "10" };

const exportColumns = [
  { header: "Partner", accessorKey: "name" },
  { header: "Contact", accessorKey: "contactName" },
  { header: "Email", accessorKey: "contactEmail" },
  { header: "Phone", accessorKey: "contactPhone" },
  { header: "Status", accessorKey: "status" },
  { header: "Share %", accessorFn: (row) => row.shareBps / 100 },
  { header: "Main-market share %", accessorFn: (row) => (row.mainEnabled ? (row.mainShareBps ?? 0) / 100 : "") },
  { header: "Page address", accessorFn: (row) => row.slug || "" },
  { header: "Markets", accessorKey: "marketCount" },
  { header: "Partner revenue (own markets)", accessorKey: "ownPartnerRevenue" },
  { header: "Partner revenue (main markets)", accessorKey: "mainPartnerRevenue" },
  { header: "Partner revenue", accessorKey: "partnerRevenue" },
  { header: "You keep", accessorKey: "tenantRetained" },
];

const PARTNER_STATUS = {
  ACTIVE: { label: "Active", tone: "green" },
  SUSPENDED: { label: "Paused", tone: "amber" },
  CLOSED: { label: "Closed", tone: "gray" },
};

function friendlyError(err, fallback) {
  switch (err?.code) {
    case "PARTNER_CLOSED":
      return "This partner is closed. Closed partners can only be viewed.";
    case "PARTNER_NAME_TAKEN":
      return "You already have a partner with that name.";
    case "PARTNER_EMAIL_IN_USE":
      return "That email already belongs to another portal account. Use a different contact email.";
    case "INVALID_PARTNER_SHARE":
      return "Enter a share between 0 and 100, with at most 2 decimals.";
    case "FORBIDDEN":
      return "Only a tenant administrator can manage partners.";
    default:
      return err?.message || fallback;
  }
}

const validShare = validSharePercent;

function MainShareFields({ enabled, value, onToggle, onValue, idPrefix }) {
  return (
    <div className="rounded-xl border border-gray-200 p-3 dark:border-white/10">
      <label htmlFor={`${idPrefix}-main`} className="flex cursor-pointer items-start gap-3">
        <input id={`${idPrefix}-main`} type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-primary" />
        <span className="text-sm font-medium text-navy-700 dark:text-white">{MAIN_SHARE_LABEL}</span>
      </label>
      {enabled && (
        <div className="mt-3">
          <InputField label="Main-market share %" type="number" min="0" max="100" step="0.01" value={value} onChange={(e) => onValue(e.target.value)} />
          <p className="ml-3 mt-1 text-xs text-gray-500">{MAIN_SHARE_HELP}</p>
        </div>
      )}
    </div>
  );
}

function mainShareText(partner) {
  return partner.mainEnabled ? `${bpsLabel(partner.mainShareBps)} on main markets` : "Main markets: off";
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString("en-KE", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Not yet";
}

export default function PartnersPage() {
  const { isTenantAdmin } = useAuth();
  const [period, setPeriod] = useState("all");
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { data: revenue, reload: reloadRevenue } = usePartnerRevenueBreakdown(period);

  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [shareEdit, setShareEdit] = useState(null);
  const [shareConfirm, setShareConfirm] = useState(false);
  const [profileEdit, setProfileEdit] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingResend, setPendingResend] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await apiGet("/portal/partners");
      setPartners(result.partners || []);
    } catch (err) {
      setError(err.message || "Failed to load partners.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const currency = revenue?.currency || "KES";
  const rows = useMemo(() => {
    const byId = new Map((revenue?.partners || []).map((r) => [r.partnerId, r]));
    return partners.map((p) => {
      const r = byId.get(p.id);
      return {
        ...p,
        marketCount: r?.marketCount ?? 0,
        net: r?.split.net ?? "0",
        ownPartnerRevenue: (r?.ownSplit ?? r?.split)?.partner ?? "0",
        mainPartnerRevenue: r?.main?.split?.partner ?? "0",
        partnerRevenue: r?.split.partner ?? "0",
        tenantRetained: r?.split.tenantRetained ?? "0",
      };
    });
  }, [partners, revenue]);

  async function afterChange(message) {
    setNotice(message);
    await load();
    reloadRevenue();
  }

  async function addPartner() {
    setAddError(null);
    setNotice(null);
    if (form.name.trim().length < 2) return setAddError("Enter the partner's name.");
    if (!form.slug) return setAddError("Choose the partner's site address.");
    if (slugProblem(form.slug)) return setAddError(slugProblem(form.slug));
    if (form.contactName.trim().length < 2) return setAddError("Enter the contact person's name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim())) return setAddError("Enter a valid contact email.");
    if (!validShare(form.sharePercent)) return setAddError("Enter a share between 0 and 100, with at most 2 decimals.");
    if (form.mainEnabled && !validShare(form.mainSharePercent)) return setAddError("Enter a main-market share between 0 and 100, with at most 2 decimals.");
    setAddBusy(true);
    try {
      const result = await apiPost("/portal/partners", {
        name: form.name.trim(),
        slug: form.slug,
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim() || null,
        sharePercent: Number(form.sharePercent),
      });
      // The main-market share is set through the share endpoint once the partner exists.
      // If that second call fails the partner is still created, so say so.
      let mainNote = "";
      if (form.mainEnabled) {
        try {
          await apiPut(`/portal/partners/${result.partner.id}/share`, {
            sharePercent: Number(form.sharePercent),
            mainEnabled: true,
            mainSharePercent: Number(form.mainSharePercent),
          });
        } catch (err) {
          mainNote = ` Their main-market share was not saved (${friendlyError(err, "unknown error")}). Use "Change share" to set it.`;
        }
      }
      setForm(EMPTY_FORM);
      setFormOpen(false);
      await afterChange(
        (result.createdUser
          ? `${result.partner.name} was added at ${partnerPageUrls(result.partner.slug).subdomain}. ${result.partner.contactEmail} has been emailed the site address, the portal link and a temporary password they must change at first sign-in.`
          : `${result.partner.name} was added at ${partnerPageUrls(result.partner.slug).subdomain}. ${result.partner.contactEmail} already had an account and can sign in with it.`) +
          mainNote +
          ` They can now add their own logo and colours from "My site" in their portal.`,
      );
    } catch (err) {
      setAddError(friendlyError(err, "Could not add that partner."));
    } finally {
      setAddBusy(false);
    }
  }

  function closeActions() {
    setShareEdit(null);
    setProfileEdit(null);
    setShareConfirm(false);
    setPendingStatus(null);
    setPendingResend(null);
    setActionError(null);
  }

  async function runAction(fn, message) {
    setActionBusy(true);
    setActionError(null);
    try {
      await fn();
      closeActions();
      await afterChange(message);
    } catch (err) {
      setActionError(friendlyError(err, "That did not work. Try again."));
    } finally {
      setActionBusy(false);
    }
  }

  function saveShare() {
    const { partner, value, mainEnabled, mainValue } = shareEdit;
    const body = { sharePercent: Number(value), mainEnabled };
    if (mainEnabled) body.mainSharePercent = Number(mainValue);
    return runAction(
      () => apiPut(`/portal/partners/${partner.id}/share`, body),
      mainEnabled
        ? `${partner.name} now gets ${Number(value)}% on their own markets and ${Number(mainValue)}% on main-market trades from their page, on revenue earned from now on.`
        : `${partner.name} now gets ${Number(value)}% on their own markets and nothing on main markets, on revenue earned from now on.`,
    );
  }

  function saveStatus() {
    const { partner, status } = pendingStatus;
    return runAction(
      () => apiPost(`/portal/partners/${partner.id}/status`, { status }),
      status === "SUSPENDED" ? `${partner.name} is paused and has been signed out. Their public page is hidden until you resume them.` : `${partner.name} is active again.`,
    );
  }

  function resendInvite() {
    const partner = pendingResend;
    return runAction(() => apiPost(`/portal/partners/${partner.id}/resend-invite`, {}), `A new temporary password was emailed to ${partner.contactEmail}.`);
  }

  const columns = [
    columnHelper.accessor("name", {
      header: "Partner",
      cell: (info) => (
        <div>
          <p className="text-sm font-semibold text-navy-700 dark:text-white">{info.getValue()}</p>
          {!info.row.original.onboardedAt && <p className="text-xs text-amber-600">Invite not accepted yet</p>}
        </div>
      ),
    }),
    columnHelper.accessor("contactName", {
      header: "Contact",
      cell: (info) => (
        <div>
          <p className="text-sm text-navy-700 dark:text-white">{info.getValue()}</p>
          <p className="text-xs text-gray-500">{info.row.original.contactEmail}</p>
        </div>
      ),
      meta: { hideBelow: "md", wrap: true },
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => {
        const status = PARTNER_STATUS[info.getValue()] || { label: info.getValue(), tone: "gray" };
        return <StatusBadge label={status.label} tone={status.tone} />;
      },
    }),
    columnHelper.accessor("shareBps", {
      header: "Share",
      cell: (info) => (
        <div>
          <p className="text-sm font-semibold text-navy-700 dark:text-white">{bpsLabel(info.getValue())}</p>
          <p className="text-xs text-gray-500">{mainShareText(info.row.original)}</p>
        </div>
      ),
    }),
    columnHelper.accessor("slug", {
      header: "Site",
      cell: (info) => {
        const urls = partnerPageUrls(info.getValue());
        return urls ? (
          <a href={urls.subdomain} target="_blank" rel="noreferrer" className="text-sm font-medium text-brand-500 hover:underline">
            {urls.subdomain.replace("https://", "")}
          </a>
        ) : (
          <p className="text-xs text-gray-500">No page</p>
        );
      },
      meta: { hideBelow: "xl" },
    }),
    columnHelper.accessor("marketCount", {
      header: "Markets",
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{info.getValue()}</p>,
      meta: { hideBelow: "lg" },
    }),
    columnHelper.accessor("partnerRevenue", {
      header: "Partner revenue",
      cell: (info) => <p className="text-sm font-semibold text-purple-600 dark:text-purple-300">{formatKes(info.getValue(), currency)}</p>,
      meta: { hideBelow: "sm" },
    }),
    columnHelper.accessor("tenantRetained", {
      header: "You keep",
      cell: (info) => <p className="text-sm font-semibold text-green-600 dark:text-green-400">{formatKes(info.getValue(), currency)}</p>,
      meta: { hideBelow: "lg" },
    }),
  ];

  const actionButton = "rounded-sm px-3 py-2 text-xs font-semibold";

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-2xl font-semibold text-navy-700 dark:text-white">Partners</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Partners create markets with you and share the revenue those markets earn, out of your 80% of the net.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodPicker value={period} onChange={setPeriod} />
          {isTenantAdmin && !formOpen && (
            <button type="button" onClick={() => setFormOpen(true)} className="rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
              Add partner
            </button>
          )}
        </div>
      </div>

      {notice && <div className="mb-4 rounded-xl bg-green-50 p-3 text-sm font-medium text-green-700 dark:bg-green-500/10 dark:text-green-300">{notice}</div>}

      {revenue?.totals && (
        <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Card extra="p-4">
            <p className="text-xs font-medium text-muted-foreground">Net from partner markets</p>
            <p className="num mt-1 text-lg font-semibold text-navy-700 dark:text-white">{formatKes(revenue.totals.net, currency)}</p>
          </Card>
          <Card extra="p-4">
            <p className="text-xs font-medium text-muted-foreground">Your share of it ({bpsLabel(revenue.tenantShareBps)})</p>
            <p className="num mt-1 text-lg font-semibold text-navy-700 dark:text-white">{formatKes(revenue.totals.tenantShare, currency)}</p>
          </Card>
          <Card extra="p-4">
            <p className="text-xs font-medium text-muted-foreground">Paid to partners</p>
            <p className="num mt-1 text-lg font-semibold text-purple-600 dark:text-purple-300">{formatKes(revenue.totals.partner, currency)}</p>
          </Card>
          <Card extra="p-4">
            <p className="text-xs font-medium text-muted-foreground">You keep</p>
            <p className="num mt-1 text-lg font-semibold text-green-600 dark:text-green-400">{formatKes(revenue.totals.tenantRetained, currency)}</p>
          </Card>
        </div>
      )}

      {isTenantAdmin && formOpen && (
        <Card extra="p-5 mb-5">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Add a partner</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            The contact is emailed a link to this portal and a temporary password they must change at first sign-in. They will only see their own markets and the revenue those markets earn.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <InputField
              label="Partner name"
              placeholder="e.g. Nairobi Sports Media"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slugTouched ? form.slug : slugFromName(e.target.value) })}
            />
            <div>
              <InputField
                label="Site address"
                placeholder="e.g. nairobi-sports"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: normalizeSlugInput(e.target.value), slugTouched: true })}
              />
              <p className={`mt-1 text-xs ${form.slug && slugProblem(form.slug) ? "text-red-500" : "text-gray-500"}`}>
                {form.slug
                  ? slugProblem(form.slug) || `Their site will be ${partnerPageUrls(form.slug).subdomain.replace("https://", "")}. Only you can change it later.`
                  : `The partner's own trading site, e.g. nairobi-sports gives nairobi-sports.${PARTNER_ROOT_DOMAIN}.`}
              </p>
            </div>
            <InputField label="Contact name" placeholder="e.g. Jane Wanjiru" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
            <InputField label="Contact email" type="email" placeholder="jane@partner.co.ke" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            <InputField label="Contact phone (optional)" placeholder="07XX XXX XXX" value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
            <div>
              <InputField label="Share %" type="number" min="0" max="100" step="0.01" value={form.sharePercent} onChange={(e) => setForm({ ...form, sharePercent: e.target.value })} />
              <p className="ml-3 mt-1 text-xs text-gray-500">{SHARE_HELP}</p>
            </div>
          </div>
          <div className="mt-4 max-w-xl">
            <MainShareFields
              idPrefix="add-partner"
              enabled={form.mainEnabled}
              value={form.mainSharePercent}
              onToggle={(mainEnabled) => setForm({ ...form, mainEnabled })}
              onValue={(mainSharePercent) => setForm({ ...form, mainSharePercent })}
            />
          </div>
          {addError && <p className="mt-3 text-sm font-medium text-red-500">{addError}</p>}
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={addPartner} disabled={addBusy} className="rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              {addBusy ? "Adding…" : "Add partner and send invite"}
            </button>
            <button
              type="button"
              onClick={() => {
                setFormOpen(false);
                setAddError(null);
                setForm(EMPTY_FORM);
              }}
              className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white"
            >
              Cancel
            </button>
          </div>
        </Card>
      )}

      {shareEdit && !shareConfirm && (
        <Card extra="p-5 mb-5 max-w-xl">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Change {shareEdit.partner.name}'s share</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Currently {bpsLabel(shareEdit.partner.shareBps)} on their own markets. {mainShareText(shareEdit.partner)}.
          </p>
          <div className="mt-4">
            <InputField label="Own-markets share %" type="number" min="0" max="100" step="0.01" value={shareEdit.value} onChange={(e) => setShareEdit({ ...shareEdit, value: e.target.value })} />
            <p className="ml-3 mt-1 text-xs text-gray-500">{SHARE_HELP}</p>
          </div>
          <div className="mt-4">
            <MainShareFields
              idPrefix="edit-share"
              enabled={shareEdit.mainEnabled}
              value={shareEdit.mainValue}
              onToggle={(mainEnabled) => setShareEdit({ ...shareEdit, mainEnabled })}
              onValue={(mainValue) => setShareEdit({ ...shareEdit, mainValue })}
            />
          </div>
          {actionError && <p className="mt-3 text-sm font-medium text-red-500">{actionError}</p>}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => {
                if (!validShare(shareEdit.value)) return setActionError("Enter a share between 0 and 100, with at most 2 decimals.");
                if (shareEdit.mainEnabled && !validShare(shareEdit.mainValue)) return setActionError("Enter a main-market share between 0 and 100, with at most 2 decimals.");
                setActionError(null);
                setShareConfirm(true);
              }}
              className="rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              Review change
            </button>
            <button type="button" onClick={closeActions} className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white">
              Cancel
            </button>
          </div>
        </Card>
      )}

      {profileEdit && (
        <PartnerProfileEditor
          key={profileEdit.id}
          partner={profileEdit}
          onChanged={() => {
            load();
          }}
          onClose={closeActions}
        />
      )}

      <ConfirmDialog
        open={Boolean(shareEdit && shareConfirm)}
        tone="primary"
        title={shareEdit ? `Change ${shareEdit.partner.name}'s revenue share?` : ""}
        description={
          shareEdit
            ? `This applies to revenue earned from now on. Revenue already earned keeps the share it was earned at. ${shareEdit.partner.name} will get ${Number(shareEdit.value)}% of your 80% of the net revenue their markets earn, and you keep ${Math.round((100 - Number(shareEdit.value)) * 100) / 100}%. ${
                shareEdit.mainEnabled
                  ? `On your main markets, they will also get ${Number(shareEdit.mainValue)}% of your 80% of the trading fees on positions opened from their page or subdomain.`
                  : "They will get nothing from trades on your main markets."
              }`
            : ""
        }
        confirmLabel="Change share"
        busy={actionBusy}
        error={actionError}
        onConfirm={saveShare}
        onCancel={closeActions}
      />
      <ConfirmDialog
        open={Boolean(pendingStatus)}
        tone={pendingStatus?.status === "SUSPENDED" ? "danger" : "success"}
        title={pendingStatus ? (pendingStatus.status === "SUSPENDED" ? `Pause ${pendingStatus.partner.name}?` : `Resume ${pendingStatus.partner.name}?`) : ""}
        description={
          pendingStatus?.status === "SUSPENDED"
            ? "Everyone on their team is signed out straight away and cannot sign in until you resume them. Their public page and subdomain go offline. Their markets and earned revenue are kept."
            : "Their team can sign in again straight away, and their public page comes back."
        }
        confirmLabel={pendingStatus?.status === "SUSPENDED" ? "Pause" : "Resume"}
        busy={actionBusy}
        error={actionError}
        onConfirm={saveStatus}
        onCancel={closeActions}
      />
      <ConfirmDialog
        open={Boolean(pendingResend)}
        tone="primary"
        title={pendingResend ? `Resend ${pendingResend.name}'s invite?` : ""}
        description={pendingResend ? `${pendingResend.contactEmail} gets a new temporary password. Any earlier one stops working.` : ""}
        confirmLabel="Resend invite"
        busy={actionBusy}
        error={actionError}
        onConfirm={resendInvite}
        onCancel={closeActions}
      />

      <div className="mt-5">
        <ExpandableDataTable
          title="Your partners"
          subtitle={
            isTenantAdmin
              ? "Expand a partner to edit their public page, change their share, pause them or resend their invite. Closed partners can only be viewed."
              : "Only a tenant administrator can add or change partners."
          }
          columns={columns}
          exportColumns={exportColumns}
          exportFilename="partners"
          data={rows}
          loading={loading}
          error={error}
          onRetry={load}
          emptyMessage={isTenantAdmin ? "No partners yet. Add your first partner above." : "No partners yet."}
          getRowId={(row) => row.id}
          rowLabel={(row) => `partner ${row.name}`}
          renderDetail={(row) => (
            <>
            <DetailGrid>
              <DetailItem label="Contact">{row.contactName}</DetailItem>
              <DetailItem label="Email">{row.contactEmail}</DetailItem>
              <DetailItem label="Phone">{row.contactPhone || "Not given"}</DetailItem>
              <DetailItem label="Share on their markets">{`${bpsLabel(row.shareBps)} of your 80%`}</DetailItem>
              <DetailItem label="Share on main markets">{row.mainEnabled ? `${bpsLabel(row.mainShareBps)} of your 80%, on trades from their page` : "Off"}</DetailItem>
              <DetailItem label="Net (own and main markets)">{formatKes(row.net, currency)}</DetailItem>
              <DetailItem label="Partner revenue: own markets">{formatKes(row.ownPartnerRevenue, currency)}</DetailItem>
              <DetailItem label="Partner revenue: main markets">{formatKes(row.mainPartnerRevenue, currency)}</DetailItem>
              <DetailItem label="Public page" span={2}>
                {partnerPageUrls(row.slug) ? (
                  <span className="flex flex-col">
                    <a href={partnerPageUrls(row.slug).page} target="_blank" rel="noreferrer" className="font-medium text-brand-500 hover:underline">
                      {partnerPageUrls(row.slug).page}
                    </a>
                    <a href={partnerPageUrls(row.slug).subdomain} target="_blank" rel="noreferrer" className="font-medium text-brand-500 hover:underline">
                      {partnerPageUrls(row.slug).subdomain}
                    </a>
                  </span>
                ) : (
                  "No address set, so no public page"
                )}
              </DetailItem>
              <DetailItem label="Branding">
                {row.logoUrl || row.logoDarkUrl || row.brandPrimary || row.brandAccent ? (
                  <span className="flex flex-wrap items-center gap-2">
                    {row.logoUrl && <img src={row.logoUrl} alt="Light logo" className="h-6 max-w-[5rem] rounded-sm bg-white object-contain" />}
                    {row.logoDarkUrl && <img src={row.logoDarkUrl} alt="Dark logo" className="h-6 max-w-[5rem] rounded-sm bg-navy-900 object-contain" />}
                    {[row.brandPrimary, row.brandAccent].filter(Boolean).map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 font-mono text-xs">
                        <span className="h-4 w-4 rounded-sm border border-gray-200 dark:border-white/10" style={{ backgroundColor: c }} />
                        {c}
                      </span>
                    ))}
                  </span>
                ) : (
                  "Not set"
                )}
              </DetailItem>
              {row.description && (
                <DetailItem label="Description" span={3}>
                  {row.description}
                </DetailItem>
              )}
              <DetailItem label="Team members">{(row.members || []).length}</DetailItem>
              <DetailItem label="Invite sent">{formatDate(row.onboardingSentAt)}</DetailItem>
              <DetailItem label="First signed in">{formatDate(row.onboardedAt)}</DetailItem>
              <DetailItem label="Added">{formatDate(row.createdAt)}</DetailItem>
            </DetailGrid>
            <PartnerOversight partnerId={row.id} />
            </>
          )}
          renderActions={
            isTenantAdmin
              ? (row) =>
                  row.status === "CLOSED" ? null : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          closeActions();
                          setProfileEdit(row);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className={`${actionButton} bg-primary text-primary-foreground`}
                      >
                        Edit public page
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          closeActions();
                          setShareEdit({
                            partner: row,
                            value: String(row.shareBps / 100),
                            mainEnabled: Boolean(row.mainEnabled),
                            mainValue: String((row.mainShareBps ?? 0) / 100),
                          });
                        }}
                        className={`${actionButton} bg-brand-50 text-brand-500 dark:bg-brand-400/10`}
                      >
                        Change share
                      </button>
                      {row.status === "ACTIVE" ? (
                        <button
                          type="button"
                          onClick={() => {
                            closeActions();
                            setPendingStatus({ partner: row, status: "SUSPENDED" });
                          }}
                          className={`${actionButton} bg-red-50 text-red-500`}
                        >
                          Pause
                        </button>
                      ) : row.status === "SUSPENDED" ? (
                        <button
                          type="button"
                          onClick={() => {
                            closeActions();
                            setPendingStatus({ partner: row, status: "ACTIVE" });
                          }}
                          className={`${actionButton} bg-green-50 text-green-600`}
                        >
                          Resume
                        </button>
                      ) : null}
                      {!row.onboardedAt && (
                        <button
                          type="button"
                          onClick={() => {
                            closeActions();
                            setPendingResend(row);
                          }}
                          className={`${actionButton} border border-gray-200 text-navy-700 dark:border-white/10 dark:text-white`}
                        >
                          Resend invite
                        </button>
                      )}
                    </>
                  )
              : undefined
          }
        />
      </div>
    </div>
  );
}
